import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["additionalEmails", "data-selenium-test", "disabled", "enablePopovers", "inputMode", "popoverOpen", "popoverProps", "readOnly", "setPopoverOpen", "showAutoSaveText"];
import { memo, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import PropertyInputMultipleEmailPopoverBody from './email/components/PropertyInputMultipleEmailPopoverBody';
import { GridItem } from 'foundations-components/transitional/components/Grid';
import unescapedText from 'I18n/utils/unescapedText';
import { TruncateString } from 'foundations-components/transitional/components/TruncateString';
import uniqueId from 'transmute/uniqueId';
import MultipleEmailInputErrorBanner from './email/components/MultipleEmailInputErrorBanner';
import { ConfirmModal } from 'foundations-components/transitional/components/ConfirmModal';
import FormattedMessage from 'I18n/components/FormattedMessage';
import promiseDone from 'hs-promise-utils/promiseDone';
import useComponentMountMetric from '../metrics/useComponentMountMetric';
import { useObjectTypeId } from '../v2/hooks/useObjectTypeId';
import { useActionsForProperty } from '../v2/hooks/useActionsForProperty';
import { usePropertyValue } from '../v2/hooks/usePropertyValue';
import usePrevious from 'react-utils/hooks/usePrevious';
import { getTransferrableProps } from '../utils/getTransferrableProps';
import { Clickable } from 'foundations-components/transitional/components/Clickable';
import { Flex } from 'foundations-components/components/Flex';
import { useIsUngatedForDisplayOptimizedFormControl } from '../v2/hooks/useIsUngatedForDisplayOptimizedFormControl';
import { EMPTY_PROPERTY_VALUE } from '../constants/EmptyPropertyValue';
import { Popover } from 'foundations-components/transitional/components/Popover';
import { DISPLAY_OPTIMIZED_BLOCK_SIZE } from '../constants/DisplayOptimizedBlockSize';
import { jsx as _jsx } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const generateId = () => {
  return uniqueId('email');
};
/**
 * Default action handler used if the parent does not provide
 * a callback function for an action. Action handlers are expected
 * to return a Promise indicating their success/failure.
 *
 * The default handler always fails, because the multiple email
 * input doesn't work unless the host app handles each callback
 * and persists changes to the BE.
 *
 * (Email changes are not simple property changes. They require
 * BE involvement to update contact identities. The `email` and
 * `hs_additional_email` properties are updated as a side-effect
 * of updating contact identities.)
 */
const emptyAction = () => Promise.reject(new Error('Not implemented'));
const emptyActions = {
  addAdditionalEmail: emptyAction,
  deleteAdditionalEmail: emptyAction,
  makeEmailPrimary: emptyAction,
  updatePrimaryEmail: emptyAction,
  updateAdditionalEmail: emptyAction
};
function useActionHook(action) {
  const actionRef = useRef(action);
  useLayoutEffect(() => {
    actionRef.current = action;
  });
  const [actionState, setActionState] = useState('IDLE');
  const memoizedAction = useCallback((...params) => {
    setActionState('LOADING');
    return actionRef.current.apply(null, params).then(() => {
      setActionState('IDLE');
    }).catch(exception => {
      setActionState('ERROR');
      throw exception;
    });
  }, []);
  const resetState = useCallback(() => {
    setActionState('IDLE');
  }, []);
  return [memoizedAction, {
    isLoading: actionState === 'LOADING',
    error: actionState === 'ERROR',
    resetState
  }];
}
const PRIMARY_EMAIL = 0;
const generateEmail = value => ({
  id: generateId(),
  isNewEmail: !value,
  value
});
const PropertyInputMultipleEmailV2 = _ref => {
  let {
      additionalEmails,
      'data-selenium-test': dataSeleniumTest,
      disabled,
      enablePopovers,
      inputMode,
      popoverOpen,
      popoverProps,
      readOnly,
      setPopoverOpen,
      showAutoSaveText = true
    } = _ref,
    rest = _objectWithoutPropertiesLoose(_ref, _excluded);
  useComponentMountMetric('PropertyInputMultipleEmailV2');
  const isUngatedForDisplayOptimizedFormControl = useIsUngatedForDisplayOptimizedFormControl();
  const objectTypeId = useObjectTypeId();
  const actions = useActionsForProperty() || emptyActions;
  const value = usePropertyValue();
  const currentEmails = useMemo(() => {
    const additionalEmailsArray = additionalEmails ? additionalEmails.split(';') : [];
    return [generateEmail(value || ''), ...additionalEmailsArray.map(email => ({
      id: generateId(),
      isNewEmail: false,
      value: email
    }))];
  }, [value, additionalEmails]);
  const prevValue = usePrevious(value);
  const prevAdditionalEmails = usePrevious(additionalEmails);
  const [emails, setEmails] = useState(currentEmails);
  const [tempDeletedEmail, setTempDeletedEmail] = useState();
  const [addAdditionalEmail, {
    isLoading: isAddAdditionalEmailPending,
    error: isAddAdditionalEmailError,
    resetState: resetAddAdditionalEmailState
  }] = useActionHook(actions.addAdditionalEmail);
  const [deleteAdditionalEmail, {
    isLoading: isDeleteAdditionalEmailPending,
    error: isDeleteAdditionalEmailError,
    resetState: resetDeleteAdditionalEmailState
  }] = useActionHook(actions.deleteAdditionalEmail);
  const [makeEmailPrimary, {
    isLoading: isMakeEmailPrimaryPending,
    error: isMakeEmailPrimaryError,
    resetState: resetMakeEmailPrimaryState
  }] = useActionHook(actions.makeEmailPrimary);
  const [updatePrimaryEmail, {
    isLoading: isUpdatePrimaryEmailPending,
    error: isUpdatePrimaryEmailError,
    resetState: resetUpdatePrimaryEmailState
  }] = useActionHook(actions.updatePrimaryEmail);
  const [updateAdditionalEmail, {
    isLoading: isUpdateAdditionalEmailPending,
    error: isUpdateAdditionalEmailError,
    resetState: resetUpdateAdditionalEmailState
  }] = useActionHook(actions.updateAdditionalEmail);
  const resetErrorState = useCallback(() => {
    resetAddAdditionalEmailState();
    resetDeleteAdditionalEmailState();
    resetMakeEmailPrimaryState();
    resetUpdatePrimaryEmailState();
    resetUpdateAdditionalEmailState();
  }, [resetAddAdditionalEmailState, resetDeleteAdditionalEmailState, resetMakeEmailPrimaryState, resetUpdateAdditionalEmailState, resetUpdatePrimaryEmailState]);
  const isLoading = isAddAdditionalEmailPending || isDeleteAdditionalEmailPending || isMakeEmailPrimaryPending || isUpdatePrimaryEmailPending || isUpdateAdditionalEmailPending;
  const hasError = isAddAdditionalEmailError || isDeleteAdditionalEmailError || isMakeEmailPrimaryError || isUpdatePrimaryEmailError || isUpdateAdditionalEmailError;

  /**
   * - As we keep track the current emails in the local state, we need to update the state on prop changes.
   * We use two useEffects as we get the `value` and `additionalEmails` props separately.
   * If we update the two at same time it could lead to an inconsistent state.
   * - We don't want to update the state if there is an ongoing async operation.
   * - We also need to check if the primary email is included in the additional emails.
   */
  const isPrimaryIncludedInAdditionalEmails = additionalEmails && value && additionalEmails.includes(value);
  useEffect(() => {
    if (!prevValue) {
      return;
    }
    if (prevValue !== value && !isLoading && !isPrimaryIncludedInAdditionalEmails) {
      setEmails(_emails => {
        const [__, ..._additionalEmails] = _emails;
        return currentEmails[PRIMARY_EMAIL] ? [currentEmails[PRIMARY_EMAIL], ..._additionalEmails] : [];
      });
    }
  }, [currentEmails, prevValue, value, isLoading, isPrimaryIncludedInAdditionalEmails]);
  useEffect(() => {
    if (!prevAdditionalEmails) {
      return;
    }
    if (prevAdditionalEmails !== additionalEmails && !isLoading && !isPrimaryIncludedInAdditionalEmails) {
      setEmails(_emails => {
        const [primaryEmail, ...___] = _emails;
        const [__, ..._additionalEmails] = currentEmails;
        return primaryEmail ? [primaryEmail, ..._additionalEmails] : [];
      });
    }
  }, [currentEmails, prevAdditionalEmails, additionalEmails, isLoading, isPrimaryIncludedInAdditionalEmails]);
  const handleLabelClicked = useCallback(() => {
    // We don't want to allow opening the popover if the input is disabled or
    // read only
    if (readOnly || disabled) {
      return;
    }
    setPopoverOpen(true);
  }, [disabled, readOnly, setPopoverOpen]);
  const handleOpenChanged = useCallback(evt => {
    setPopoverOpen(evt.target.value);
  }, [setPopoverOpen]);
  const handleAdditionalEmailInputAdded = useCallback(() => {
    setEmails(_emails => [..._emails, {
      id: generateId(),
      isNewEmail: true,
      value: ''
    }]);
  }, []);
  const handleAdditionalEmailDeleted = useCallback(email => {
    promiseDone(deleteAdditionalEmail(email.value).then(() => {
      setEmails(_emails => [..._emails.filter(_email => _email.id !== email.id)]);
      setTempDeletedEmail(undefined);
    }).catch(() => {}));
  }, [deleteAdditionalEmail]);
  const handleAdditionalEmailSaved = useCallback((email, oldEmail) => {
    const promise = email.isNewEmail ? addAdditionalEmail(email.value) : updateAdditionalEmail(oldEmail.value, email.value);
    promiseDone(promise.then(() => {
      setEmails(_emails => _emails.map(_email => _email.id === email.id ? Object.assign({}, email, {
        isNewEmail: false
      }) : _email));
    }).catch(() => {}));
  }, [updateAdditionalEmail, addAdditionalEmail]);
  const handlePromoteToPrimary = useCallback(email => {
    promiseDone(makeEmailPrimary(email.value).then(() => {
      setEmails(_emails => {
        const filteredEmails = _emails.filter(_email => _email.id !== email.id);
        return [email, ...filteredEmails];
      });
    }).catch(() => {}));
  }, [makeEmailPrimary]);
  const handlePrimaryEmailInputAdded = useCallback(() => {
    setEmails(_emails => {
      return [{
        id: generateId(),
        isNewEmail: true,
        value: ''
      }, ..._emails];
    });
  }, []);
  const handlePrimaryEmailDeleted = useCallback(email => {
    promiseDone(updatePrimaryEmail('').then(() => {
      setEmails(_emails => {
        return _emails.filter(_email => _email.id !== email.id);
      });
      setTempDeletedEmail(undefined);
    }).catch(() => {}));
  }, [updatePrimaryEmail]);
  const handlePrimaryEmailSaved = useCallback(email => {
    promiseDone(updatePrimaryEmail(email.value).then(() => {
      setEmails(_emails => _emails.map(_email => _email.id === email.id ? Object.assign({}, email, {
        isNewEmail: false
      }) : _email));
    }).catch(() => {}));
  }, [updatePrimaryEmail]);
  const handleDeleteEmailStarted = useCallback(email => {
    if (email.isNewEmail) {
      setEmails(_emails => _emails.filter(_email => _email.id !== email.id));
    } else {
      setTempDeletedEmail(email);
    }
  }, []);
  const handleDeleteEmailConfirmed = useCallback(() => {
    if (!tempDeletedEmail) {
      return;
    }

    // This handler is called with both primary emails and additional emails.
    // Since we don't know which one it is we check the ids to figure out if
    // it's the primary email.
    if (emails[PRIMARY_EMAIL] && tempDeletedEmail.id === emails[PRIMARY_EMAIL].id) {
      handlePrimaryEmailDeleted(tempDeletedEmail);
    } else {
      handleAdditionalEmailDeleted(tempDeletedEmail);
    }
  }, [emails, handleAdditionalEmailDeleted, handlePrimaryEmailDeleted, tempDeletedEmail]);
  const handleDeleteEmailCanceled = useCallback(() => {
    setTempDeletedEmail(undefined);
  }, []);
  const [primaryEmail, ..._additionalEmails] = emails;
  const popoverContent = /*#__PURE__*/_jsxs(_Fragment, {
    children: [hasError && /*#__PURE__*/_jsx(MultipleEmailInputErrorBanner, {
      onClose: resetErrorState
    }), /*#__PURE__*/_jsx(PropertyInputMultipleEmailPopoverBody, {
      additionalEmails: _additionalEmails,
      disabled: isLoading,
      enablePopovers: enablePopovers,
      inputMode: inputMode,
      primaryEmail: primaryEmail,
      objectTypeId: objectTypeId,
      onPrimaryEmailInputAdded: handlePrimaryEmailInputAdded,
      onPrimaryEmailDeleted: handleDeleteEmailStarted,
      onPrimaryEmailSaved: handlePrimaryEmailSaved,
      onPromoteToPrimary: handlePromoteToPrimary,
      onAdditionalEmailInputAdded: handleAdditionalEmailInputAdded,
      onAdditionalEmailDeleted: handleDeleteEmailStarted,
      onAdditionalEmailSaved: handleAdditionalEmailSaved,
      showAutoSaveText: showAutoSaveText
    })]
  });
  const isEmailsListEmpty = emails.length === 1 && emails[0].value === '';
  const emailsForDisplay = isUngatedForDisplayOptimizedFormControl && isEmailsListEmpty ? EMPTY_PROPERTY_VALUE : emails.filter(Boolean).map(email => email.value).join(unescapedText('customerDataProperties.PropertyInputMultipleEmailV2.commaSeparatorKey'));
  return /*#__PURE__*/_jsxs(GridItem, {
    size: {
      xs: 12
    },
    paddingInline: "0",
    children: [/*#__PURE__*/_jsx(Popover, Object.assign({
      paddingBlockEnd: "200",
      closeOnOutsideClick: true,
      inlineSize: "500px",
      placement: "right"
    }, popoverProps, {
      open: popoverOpen,
      content: popoverContent,
      onOpenChange: handleOpenChanged,
      children: /*#__PURE__*/_jsx(Clickable, Object.assign({}, getTransferrableProps(rest, ['onChange']), {
        onClick: handleLabelClicked,
        "data-selenium-test": dataSeleniumTest,
        "data-test-id": "multiple-email-input",
        children: /*#__PURE__*/_jsx(Flex, {
          blockSize: DISPLAY_OPTIMIZED_BLOCK_SIZE,
          alignItems: "center",
          children: /*#__PURE__*/_jsx(TruncateString, {
            children: emailsForDisplay
          })
        })
      }))
    })), tempDeletedEmail && /*#__PURE__*/_jsx(ConfirmModal, {
      confirmLabel: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "customerDataProperties.PropertyInputMultipleEmailV2.delete.confirmLabel"
      }),
      confirmUse: "danger",
      description: /*#__PURE__*/_jsx("div", {
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "customerDataProperties.PropertyInputMultipleEmailV2.delete.description",
          options: {
            email: tempDeletedEmail.value
          }
        })
      }),
      message: /*#__PURE__*/_jsx("span", {
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "customerDataProperties.PropertyInputMultipleEmailV2.delete.title"
        })
      }),
      onConfirm: handleDeleteEmailConfirmed,
      onReject: handleDeleteEmailCanceled,
      rejectLabel: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "customerDataProperties.PropertyInputMultipleEmailV2.delete.rejectLabel"
      }),
      size: "auto",
      use: "danger"
    })]
  });
};
export default /*#__PURE__*/memo(PropertyInputMultipleEmailV2);